/**
 * Copies an HTML string to the clipboard while preserving formatting.
 *
 * **Why this workaround exists**
 *
 * Outlook add-ins run inside a sandboxed iframe that does not include the
 * `clipboard-write` permissions policy. As a result, calls to
 * `navigator.clipboard` throw a DOMException and fail silently.
 *
 * This limitation is controlled by the Outlook host and cannot be overridden
 * by the add-in. Microsoft has acknowledged the issue but currently treats it
 * as a feature request with no committed fix:
 * https://techcommunity.microsoft.com/idea/microsoft365developerplatform/outlook-addin---enable-clipboard-api/2626853
 *
 * Related Chromium documentation:
 * https://www.chromium.org/Home/chromium-security/deprecating-permissions-in-cross-origin-iframes/
 *
 * **Implementation note**
 *
 * This utility uses `document.execCommand('copy')` as a fallback. Although
 * deprecated, it remains the only reliable method for copying content within
 * Outlook add-ins (both OWA and desktop) due to the iframe restrictions.
 *
 * This should be considered a temporary workaround until a supported
 * Clipboard API solution becomes available.
 */
export type HtmlSanitizer = (html: string) => string;

const identitySanitizer: HtmlSanitizer = (html) => html;

export async function copyHtmlToClipboard(
  summaryHtml: string,
  sanitizeHtml: HtmlSanitizer = identitySanitizer,
): Promise<void> {
  const safeHtml = sanitizeHtml(summaryHtml);

  if (copyWithExecCommand(safeHtml)) {
    return;
  }

  let clipboardError: unknown;

  if (navigator.clipboard?.write && typeof ClipboardItem !== 'undefined') {
    try {
      const blob = new Blob([safeHtml], { type: 'text/html' });
      const clipboardItem = new ClipboardItem({ 'text/html': blob });
      await navigator.clipboard.write([clipboardItem]);
      return;
    } catch (error) {
      clipboardError = error;
    }
  }

  if (navigator.clipboard?.writeText) {
    try {
      await navigator.clipboard.writeText(getTextFromHtml(safeHtml));
      return;
    } catch (error) {
      clipboardError = error;
    }
  }

  throw clipboardError ?? new Error('clipboard-copy-failed');
}

function getTextFromHtml(html: string): string {
  const container = document.createElement('div');
  container.innerHTML = html;

  return container.innerText ?? container.textContent ?? '';
}

function copyWithExecCommand(html: string): boolean {
  if (!html) {
    return false;
  }

  const div = document.createElement('div');
  div.contentEditable = 'true';
  div.innerHTML = html;
  div.style.position = 'fixed';
  div.style.opacity = '0';
  div.style.pointerEvents = 'none';
  document.body.appendChild(div);

  const selection = window.getSelection();
  const priorRanges: Range[] = [];

  if (selection) {
    for (let i = 0; i < selection.rangeCount; i++) {
      priorRanges.push(selection.getRangeAt(i).cloneRange());
    }
  }

  try {
    const range = document.createRange();
    range.selectNodeContents(div);
    if (!selection) {
      return false;
    }
    selection.removeAllRanges();
    selection.addRange(range);
    return document.execCommand('copy');
  } finally {
    document.body.removeChild(div);
    const sel = window.getSelection();
    if (sel) {
      sel.removeAllRanges();
      priorRanges.forEach((range) => {
        sel.addRange(range);
      });
    }
  }
}
